import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Plus,
  Search,
  Building2,
  Calendar,
  User,
  CheckCircle2,
  Camera,
  FileCheck,
  MessageSquare,
} from 'lucide-react';
import { SiteSurveyItem } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface SiteSurveyPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const SiteSurveyPage: React.FC<SiteSurveyPageProps> = ({ onNavigate }) => {
  const [surveys, setSurveys] = useState<SiteSurveyItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  const [form, setForm] = useState({
    projectId: '',
    branchId: '',
    surveyDate: new Date().toISOString().split('T')[0],
    engineer: 'Engr. Bilal Hashmi',
    existingCondition: '',
    measurements: '',
    requiredWork: '',
    requiredMaterials: '',
    remarks: '',
    signatureName: 'Engr. Bilal Hashmi / UBL BM',
    status: 'SUBMITTED',
  });

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const [sRes, pRes, bRes] = await Promise.all([
        fetch('/api/surveys'),
        fetch('/api/projects'),
        fetch('/api/branches'),
      ]);
      if (sRes.ok) {
        const sData = await sRes.json();
        if (Array.isArray(sData)) setSurveys(sData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setForm((prev) => ({
              ...prev,
              projectId: String(pData[0].id),
              branchId: String(pData[0].branchId || 1),
            }));
          }
        }
      }
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData)) setBranches(bData);
      }
    } catch (err) {
      console.warn('Surveys fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/surveys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchSurveys();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = (Array.isArray(surveys) ? surveys : []).filter((s) => {
    return (
      s.surveyNumber.toLowerCase().includes(search.toLowerCase()) ||
      (s.branchName && s.branchName.toLowerCase().includes(search.toLowerCase())) ||
      (s.engineer && s.engineer.toLowerCase().includes(search.toLowerCase())) ||
      (s.projectName && s.projectName.toLowerCase().includes(search.toLowerCase()))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Site Survey &amp; Measurements
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              Mobile Friendly Form
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Initial field assessment, condition inspection, measurement sheets &amp; required materials.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>New Site Survey</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-slate-200">
        <Search className="w-4 h-4 text-slate-400 ml-1" />
        <input
          type="text"
          className="w-full text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden"
          placeholder="Search survey # (e.g. SS-2026-001), branch, site engineer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Survey Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((s) => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-blue-900">{s.surveyNumber}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                {s.status}
              </span>
            </div>

            <div>
              <div className="font-bold text-sm text-slate-900">{s.projectName}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span>{s.branchName} ({s.branchCode})</span>
              </div>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-2 text-xs">
              <div>
                <span className="font-semibold text-slate-700">Existing Condition:</span>
                <p className="text-slate-600 mt-0.5 leading-relaxed">{s.existingCondition}</p>
              </div>

              <div>
                <span className="font-semibold text-slate-700">Measurements:</span>
                <p className="text-slate-600 mt-0.5 font-mono text-[11px]">{s.measurements}</p>
              </div>

              <div>
                <span className="font-semibold text-slate-700">Required Materials:</span>
                <p className="text-slate-600 mt-0.5">{s.requiredMaterials}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                  <span>{s.engineer}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span>{s.surveyDate}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShareData({
                    title: `Site Survey Report - ${s.surveyNumber}`,
                    docType: 'SITE_SURVEY',
                    referenceNumber: s.surveyNumber,
                    projectName: s.projectName,
                    branchName: `${s.branchName} (${s.branchCode})`,
                    date: s.surveyDate,
                    detailsSummary: `Surveyor: ${s.engineer}. Measurements: ${s.measurements}. Existing Condition: ${s.existingCondition}. Required Work: ${s.requiredWork}`,
                  });
                  setWhatsAppModalOpen(true);
                }}
                className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors ml-auto"
                title="Send site survey via WhatsApp"
              >
                <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                <span>WhatsApp Survey</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Survey Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-xl w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Record Branch Site Survey</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Project *</label>
                  <select
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.projectId}
                    onChange={(e) => {
                      const prj = projects.find((p) => p.id === Number(e.target.value));
                      setForm({
                        ...form,
                        projectId: e.target.value,
                        branchId: prj ? String(prj.branchId) : form.branchId,
                      });
                    }}
                  >
                    {(Array.isArray(projects) ? projects : []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectNumber} - {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Survey Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.surveyDate}
                    onChange={(e) => setForm({ ...form, surveyDate: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Site Engineer Name *</label>
                <input
                  type="text"
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.engineer}
                  onChange={(e) => setForm({ ...form, engineer: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Existing Branch Condition *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Describe damage, peeling paint, broken tiles, ceiling leakages..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.existingCondition}
                  onChange={(e) => setForm({ ...form, existingCondition: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Measurements &amp; Dimensions *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="e.g. Floor area: 1,850 sq.ft; Ceiling height: 11 ft; Counter length: 24 running ft..."
                  className="w-full border border-slate-200 rounded-lg p-2 font-mono"
                  value={form.measurements}
                  onChange={(e) => setForm({ ...form, measurements: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Required Civil &amp; Finishing Scope *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="e.g. Strip vinyl, 600x600 porcelain tiles, 2x2 acoustic ceiling, 3 coats paint..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.requiredWork}
                  onChange={(e) => setForm({ ...form, requiredWork: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Estimated Materials Required *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 120 bags cement, 60 boxes porcelain tiles, 8 drums paint"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.requiredMaterials}
                  onChange={(e) => setForm({ ...form, requiredMaterials: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Joint Sign-off Rep</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.signatureName}
                    onChange={(e) => setForm({ ...form, signatureName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Status</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="SUBMITTED">Submitted for BOQ Estimation</option>
                    <option value="DRAFT">Draft Work In Progress</option>
                    <option value="APPROVED">Approved by UBL Premises</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Save &amp; Submit Survey
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        data={shareData}
      />
    </div>
  );
};
